<template>
  <div>
    <div class="grid grid-cols-2 gap-6 desktop:grid-cols-5">
      <div class="desktop:col-span-2">
        <div v-if="!externalLocale">
          <div class="mb-0.5 txt-body-s-bold text-main">{{ t('Reviews.product.campaign.form.locale.label') }}</div>
          <div class="mb-0.5">
            <VuelidateField :validation="v$.locale">
              <template #default="{ state }">
                <fieldset id="campaign-locale" class="flex gap-x-3">
                  <RadioCard v-model="v$.locale.$model" value="de" :state="state">
                    {{ t('Reviews.product.campaign.form.locale.input.de') }}
                  </RadioCard>
                  <RadioCard v-model="v$.locale.$model" value="en" :state="state">
                    {{ t('Reviews.product.campaign.form.locale.input.en') }}
                  </RadioCard>
                </fieldset>
              </template>
            </VuelidateField>
          </div>
          <div class="mb-3 txt-label-m text-subtlest">{{ t('Reviews.product.campaign.form.locale.hint') }}</div>
        </div>
        <div v-if="externalLocale" class="mb-3 text-body-s text-main">{{ t('Reviews.product.campaign.form.inviteMessage.hint') }}</div>
        <div class="col-span-3">
          <VuelidateField :validation="v$.inviteMessage">
            <template #default="{ error, state, countHint }">
              <FieldWrapper
                for="campaign-inviteMessage"
                :count-hint="countHint"
                :hint="t('Reviews.product.campaign.form.inviteMessage.input.hint', { min: TEXT_MIN_LENGTH, max: TEXT_MAX_LENGTH })"
                :error="error"
              >
                <template #default="{ id }">
                  <TextareaField :id="id" v-model="v$.inviteMessage.$model" rows="8" :state="state" />
                </template>
              </FieldWrapper>
            </template>
          </VuelidateField>
          <button class="btn-outline-neutral-xs mt-3 mr-2" @click="resetInviteMessage">
            <Undo2Icon class="lucide-icon-s" />
            {{ t('Reviews.product.action.resetInviteMessage') }}
          </button>
        </div>
      </div>
      <div class="desktop:col-span-3">
        <PersonalizationPreviewBox :invite-message="v$.inviteMessage.$model" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { FieldWrapper, RadioCard, TextareaField } from '@ramp106/omrjs-core-ui'
import { Undo2Icon } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate } from '@vuelidate/core'
import { computed, reactive, ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  useReviewsCampaignPersonalizationFormOptions,
  TEXT_MIN_LENGTH,
  TEXT_MAX_LENGTH,
} from '@/composables/useReviewsCampaignPersonalizationFormOptions'
import type { ProductData, IncentivizationCampaignPersonalizationLocaleEnum } from '@/gql/reviews'
import { maxLength, minLength, required } from '@/services/validators.ts'
import VuelidateField from '@/ui/VuelidateField.vue'

import PersonalizationPreviewBox from './PersonalizationPreviewBox.vue'
import type { CampaignFormData, CampaignPersonalizationFormData } from './types'

const props = defineProps<{
  campaign: Pick<CampaignFormData, 'incentivizationType' | 'incentive' | 'amount'>
  initialData: CampaignPersonalizationFormData
  product: ProductData
  externalLocale?: IncentivizationCampaignPersonalizationLocaleEnum
}>()
const emit = defineEmits<{
  (e: 'change', value: CampaignPersonalizationFormData): void
}>()

const { t } = useI18n()

const formData = reactive<CampaignPersonalizationFormData>({
  ...props.initialData,
})

const { getDefaultInviteMessage } = useReviewsCampaignPersonalizationFormOptions(
  props.campaign,
  props.product,
  computed(() => formData.locale),
)

const rules = computed(() => ({
  locale: { required },
  inviteMessage: {
    required,
    minLength: minLength(TEXT_MIN_LENGTH),
    maxLength: maxLength(TEXT_MAX_LENGTH),
  },
}))

const $externalResults = ref<Partial<Record<keyof CampaignPersonalizationFormData, string>>>({})

const v$ = useVuelidate(rules, formData, { $externalResults })

function updateExternalErrors(errors: Partial<Record<keyof CampaignPersonalizationFormData, string>>) {
  $externalResults.value = errors
}

defineExpose({
  updateExternalErrors,
  validate: () => v$.value.$validate(),
  isValid: computed(() => !v$.value.$invalid),
  clearExternalErrors: () => v$.value.$clearExternalResults(),
})

function resetInviteMessage() {
  formData.inviteMessage = getDefaultInviteMessage()
  v$.value.inviteMessage.$reset()
}

onMounted(() => {
  if (!formData.inviteMessage && !v$.value.inviteMessage.$dirty) {
    formData.inviteMessage = getDefaultInviteMessage()
  }
})

watch(
  () => formData.locale,
  () => {
    formData.inviteMessage = getDefaultInviteMessage()
  },
)

watch(
  () => [props.campaign.incentive, props.campaign.amount, props.campaign.incentivizationType],
  () => {
    // Only update if the message field hasn't been manually edited
    if (!v$.value.inviteMessage.$dirty) {
      formData.inviteMessage = getDefaultInviteMessage()
    }
  },
  { deep: true },
)

// Sync locale from external prop
watch(
  () => props.externalLocale,
  (newLocale) => {
    if (newLocale) {
      formData.locale = newLocale
    }
  },
  { immediate: true },
)

watch(
  formData,
  () => {
    emit('change', { ...formData })
  },
  { deep: true },
)
</script>
